import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import _range from 'lodash/range';

import { intlMessage, requiredRule } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import 'components/Form/Input/Input.scss';

import Label from '../Label/Label';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { makeValidators } from '../helpers';
import './Textarea.scss';

const MIN_LINES = 2;
const MAX_LINES = 10;

const { block, element } = bem('hook-form-textarea');

const Textarea = props => {
  const {
    autoFocus,
    className,
    defaultValue,
    disabled,
    heightInLines,
    label,
    name,
    optional,
    placeholder,
    maxLength,
    required,
    showRequiredLabel,
  } = props;

  const intl = useIntl();
  const {
    register,
    formState: { errors },
    setFocus,
  } = useFormContext();

  useEffect(() => {
    if (autoFocus) {
      setFocus(name);
    }
  }, [setFocus]);

  const validators = makeValidators(props, intl);
  const hasError = Object.keys(errors).includes(name);

  return (
    <div className={block(className)}>
      <Label
        name={name}
        message={label}
        optional={optional}
        required={required}
        showRequiredLabel={showRequiredLabel}
      />
      <textarea
        className={element('textarea', {
          hasError,
          [`height-in-lines-${heightInLines}`]: heightInLines,
        })}
        placeholder={placeholder ? intl.formatMessage(placeholder) : ''}
        maxLength={maxLength}
        disabled={disabled}
        {...register(name, { ...validators, defaultValue, disabled })}
      />
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
};

Textarea.defaultProps = {
  heightInLines: 8,
};

Textarea.propTypes = {
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  heightInLines: PropTypes.oneOf(_range(MIN_LINES, MAX_LINES + 1)),
  label: intlMessage,
  name: PropTypes.string.isRequired,
  optional: PropTypes.bool,
  placeholder: intlMessage,
  maxLength: PropTypes.number,
  required: requiredRule,
  showRequiredLabel: PropTypes.bool,
};

export default Textarea;
